import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { DependenciesApi } from '@/lib/api/item-store/dependencies';
import { invalidateAfterDependencyChange } from '@/lib/utils/itemCacheUtils';
import { InvalidDependencyPairError, SelfDependencyError } from './dependencyErrors';

interface DeleteDependencyVariables {
  predecessorId: number;
  successorId: number;
}

interface UseDeleteDependencyMutationParams {
  currentItemId: number;
  options?: Omit<UseMutationOptions<void, Error, DeleteDependencyVariables>, 'mutationFn'>;
}

export const useDeleteDependencyMutation = ({
  currentItemId,
  options,
}: UseDeleteDependencyMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, DeleteDependencyVariables>({
    ...options,
    mutationFn: async ({ predecessorId, successorId }) => {
      const isPredecessorCurrent = predecessorId === currentItemId;
      const isSuccessorCurrent = successorId === currentItemId;
      if (isPredecessorCurrent && isSuccessorCurrent) {
        throw new SelfDependencyError();
      }
      if (!isPredecessorCurrent && !isSuccessorCurrent) {
        throw new InvalidDependencyPairError();
      }
      const otherId = isPredecessorCurrent ? successorId : predecessorId;
      const body = isPredecessorCurrent
        ? { itemId: currentItemId, successorId: otherId }
        : { itemId: currentItemId, predecessorId: otherId };
      await DependenciesApi.deleteDependency(body);
    },
    onSuccess: (data, variables, ...rest) => {
      const otherId =
        variables.predecessorId === currentItemId ? variables.successorId : variables.predecessorId;
      // Defer invalidation to the next microtask so the caller's UI updates
      // commit before suspense queries refetch.
      queueMicrotask(() => invalidateAfterDependencyChange(queryClient, currentItemId, otherId));
      options?.onSuccess?.(data, variables, ...rest);
    },
  });
};
